// src/pages/subscription/BundleSubscriptionListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useSubscriptionBundle, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function subscriptionStatusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'BUNDLE_SUBSCRIPTION_STATUS_TRIAL': return 'primary';
    case 'BUNDLE_SUBSCRIPTION_STATUS_ACTIVE': return 'success';
    case 'BUNDLE_SUBSCRIPTION_STATUS_PAST_DUE': return 'warning';
    case 'BUNDLE_SUBSCRIPTION_STATUS_CANCELLED': return 'error';
    case 'BUNDLE_SUBSCRIPTION_STATUS_SUSPENDED': return 'warning';
    case 'BUNDLE_SUBSCRIPTION_STATUS_EXPIRED': return 'secondary';
    default: return 'secondary';
  }
}

export const BundleSubscriptionListPage = observer(() => {
  const store = useSubscriptionBundle();
  const auth = useAuth();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [subscriptions, setSubscriptions] = useState<any[]>([]);

  // ListBundleSubscriptions is now scoped to a tenant.
  const tenantId = auth.user?.entity?.tenant_id || '';

  const load = () => {
    if (!tenantId) return;
    store.ListBundleSubscriptions(tenantId).then((res) => {
      const list = res?.subscriptions?.items;
      setSubscriptions(Array.isArray(list) ? list : []);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tenantId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this bundle subscription?')) return;
    try {
      await store.CancelBundleSubscription(id);
      load();
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Bundle Subscriptions</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Bundle Subscriptions</span>
        </nav>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Bundle</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Total Price</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : subscriptions.length === 0 ? (
              <TableEmpty colSpan={7} message="No bundle subscriptions found" />
            ) : (
              subscriptions.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link to={`/subscriptions/bundles/subscriptions/${itemId}`} className="text-primary hover:underline">
                        {String(item.bundle_name ?? item.bundle_id ?? '--')}
                      </Link>
                    </TableCell>
                    <TableCell>{String(item.tenant_name ?? item.tenant_id ?? '--')}</TableCell>
                    <TableCell>
                      <Badge variant={subscriptionStatusVariant(item.status)} size="sm">
                        {formatLabel(item.status || '', 'BUNDLE_SUBSCRIPTION_STATUS_')}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatMoney(item.total_price)}</TableCell>
                    <TableCell>{formatMoney(item.discount_amount)}</TableCell>
                    <TableCell>
                      {formatDate(item.current_period_start)} - {formatDate(item.current_period_end)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/bundles/subscriptions/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                        {item.status !== 'BUNDLE_SUBSCRIPTION_STATUS_CANCELLED' && item.status !== 'BUNDLE_SUBSCRIPTION_STATUS_EXPIRED' && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(itemId)}>{t('common.cancel')}</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default BundleSubscriptionListPage;
